import Pusher, { Channel } from "pusher-js";

class PusherClient {
  private static instance: PusherClient;
  private pusher: Pusher | null = null;
  private channels: Map<string, Channel> = new Map();
  private isInitialized = false;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): PusherClient {
    if (!PusherClient.instance) {
      PusherClient.instance = new PusherClient();
    }
    return PusherClient.instance;
  }

  public initialize(config: {
    key: string;
    cluster: string;
    authEndpoint: string;
  }): void {
    if (this.isInitialized) {
      console.warn("[Pusher] Already initialized");
      return;
    }

    try {
      this.pusher = new Pusher(config.key, {
        cluster: config.cluster,
        authEndpoint: config.authEndpoint,
        forceTLS: true,
        enabledTransports: ["ws", "wss"],
        disabledTransports: ["xhr_streaming", "xhr_polling"],
      });

      this.pusher.connection.bind("connected", () => {
        console.log("[Pusher] Connected successfully");
      });

      this.pusher.connection.bind("disconnected", () => {
        console.warn("[Pusher] Disconnected");
      });

      this.pusher.connection.bind("error", (err: Error) => {
        console.error("[Pusher] Connection error:", err);
      });

      this.isInitialized = true;
    } catch (error) {
      console.error("[Pusher] Initialization failed:", error);
      throw error;
    }
  }

  public subscribe(channelName: string): Channel | null {
    if (!this.pusher) {
      console.error("[Pusher] Not initialized");
      return null;
    }

    if (this.channels.has(channelName)) {
      return this.channels.get(channelName)!;
    }

    try {
      const channel = this.pusher.subscribe(channelName);

      channel.bind("pusher:subscription_succeeded", () => {
        console.log(`[Pusher] Subscribed to ${channelName}`);
      });

      channel.bind("pusher:subscription_error", (error: Error) => {
        console.error(`[Pusher] Subscription error for ${channelName}:`, error);
      });

      this.channels.set(channelName, channel);
      return channel;
    } catch (error) {
      console.error(`[Pusher] Failed to subscribe to ${channelName}:`, error);
      return null;
    }
  }

  public unsubscribe(channelName: string): void {
    if (!this.pusher) {
      return;
    }

    try {
      this.pusher.unsubscribe(channelName);
      this.channels.delete(channelName);
      console.log(`[Pusher] Unsubscribed from ${channelName}`);
    } catch (error) {
      console.error(
        `[Pusher] Failed to unsubscribe from ${channelName}:`,
        error
      );
    }
  }

  public disconnect(): void {
    if (!this.pusher) {
      return;
    }

    try {
      this.channels.forEach((_, channelName) => {
        this.unsubscribe(channelName);
      });

      this.pusher.disconnect();
      this.pusher = null;
      this.isInitialized = false;
      console.log("[Pusher] Disconnected successfully");
    } catch (error) {
      console.error("[Pusher] Disconnect error:", error);
    }
  }

  public onStateChange(
    callback: (states: { previous: string; current: string }) => void
  ): void {
    this.pusher?.connection.bind("state_change", callback);
  }

  public offStateChange(
    callback: (states: { previous: string; current: string }) => void
  ): void {
    this.pusher?.connection.unbind("state_change", callback);
  }

  public getConnectionState(): string {
    return this.pusher?.connection.state ?? "disconnected";
  }

  public isConnected(): boolean {
    return this.pusher?.connection.state === "connected";
  }
}

export const pusherClient = PusherClient.getInstance();
